<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createInternetArchiveConfig } from "./fs";
  import { iaConnect, makeFileSystemInternetArchive } from "./fs-internetarchive";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createInternetArchiveConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(config.accessKey.trim() != "" && config.secretKey.trim() != "" && !testingConnection);

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing Internet Archive connection");
    try {
      config.accessKey = config.accessKey.trim();
      config.secretKey = config.secretKey.trim();
      config.name = config.name.trim();
      config.account = await iaConnect(config.accessKey, config.secretKey);
      let fs = makeFileSystemInternetArchive(config);
      let who = config.name || "Internet Archive";
      telegramNotify(`New Internet Archive connection for ${who}`);
      captureEvent("internetarchive-login", {});
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect Internet Archive">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-internetarchive-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-internetarchive-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-internetarchive-access">Access key:</label>
      <input
        type="text"
        id="input-internetarchive-access"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.accessKey}
      />

      <label class="mt-3" for="input-internetarchive-secret">Secret key:</label>
      <input
        type="password"
        id="input-internetarchive-secret"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.secretKey}
      />

      <div class="mt-3 w-72 text-gray-500">
        Get the keys from your
        <a href="https://archive.org/account/s3.php" class="text-blue-600 underline" target="_blank" rel="noreferrer"
          >Internet Archive account</a
        >. The top folder lists your items. What you store is public, shows after a few minutes, and a new folder at the
        top makes an item, which cannot be removed.
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Internet Archive</button>
    </div>
  {/snippet}
</DialogBase>
